// Inline-SVG line chart with a soft area fill. Computes a cumulative
// running total from per-day buckets so the visualisation
// communicates "is the curve bending up?" — distinct from
// Sparkline's "did anything happen each day?".

export type DayPoint = { day: string; count: number };

const WIDTH = 320;
const HEIGHT = 96;
const PAD = 4;

export function TrendLine({
  data,
  label,
  accent = "currentColor",
}: {
  data: DayPoint[];
  label: string;
  accent?: string;
}) {
  // Cumulative running totals over the per-day buckets.
  let acc = 0;
  const cumulative = data.map((d) => {
    acc += d.count;
    return { day: d.day, total: acc };
  });
  const max = Math.max(...cumulative.map((p) => p.total), 1);
  const innerW = WIDTH - PAD * 2;
  const innerH = HEIGHT - PAD * 2;
  const xAt = (i: number) =>
    PAD + (cumulative.length === 1 ? innerW / 2 : (i / (cumulative.length - 1)) * innerW);
  const yAt = (v: number) => PAD + innerH - (v / max) * innerH;

  const linePoints = cumulative.map((p, i) => `${xAt(i)},${yAt(p.total)}`).join(" ");
  // Closed polygon for the area: line, then bottom-right -> bottom-left.
  const areaPoints = `${linePoints} ${xAt(cumulative.length - 1)},${HEIGHT - PAD} ${xAt(0)},${HEIGHT - PAD}`;
  const final = cumulative[cumulative.length - 1]?.total ?? 0;

  return (
    <div className="border border-ink-800 rounded-md p-4 bg-ink-900/30">
      <div className="flex items-baseline justify-between mb-2">
        <span className="font-mono text-[10px] uppercase tracking-widest text-ink-400">
          {label}
        </span>
        <span className="text-sm tabular-nums text-ink-100">{final}</span>
      </div>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        width="100%"
        height={HEIGHT}
        className="overflow-visible"
        aria-label={label}
      >
        <polygon points={areaPoints} fill={accent} opacity={0.18} />
        <polyline
          points={linePoints}
          fill="none"
          stroke={accent}
          strokeWidth={1.5}
          opacity={0.9}
        />
        {/* Hover targets — invisible rectangles per data point so
            tooltips work everywhere along the line. */}
        {cumulative.map((p, i) => (
          <rect
            key={p.day}
            x={xAt(i) - 6}
            y={PAD}
            width={12}
            height={innerH}
            fill="transparent"
          >
            <title>{`${p.day}: ${p.total}`}</title>
          </rect>
        ))}
      </svg>
      <div className="mt-1 flex justify-between font-mono text-[9px] text-ink-500">
        <span>{cumulative[0]?.day ?? ""}</span>
        <span>{cumulative[cumulative.length - 1]?.day ?? ""}</span>
      </div>
    </div>
  );
}
